import _, {intersection, partition, uniq} from 'lodash';
import {createSelector} from 'reselect';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {isTeammateConversation} from '../../../helpers/models/conversationHelpers';
import {wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {TagBehaviorsEnum} from '../../../models/api/tagModel';
import {AppState} from '../../appState';
import {ConversationBasesSelector} from '../conversations/conversationsSelectors';
import {getFlatDelegationOwners} from '../delegations/delegationsSelectors';
import {getUserOwnedInboxes} from '../inboxes/inboxesSelectors';
import {getFlatTeammates, getUser, getUserNamespace} from '../teammates/teammatesSelectors';
import {getUserTeams} from '../teams/teamsSelectors';

export function getTags(state: AppState) {
  return state.data.tags;
}

export const getUserAndDelegatedInboxTagIds = createSelector(
  [getUser, getFlatDelegationOwners],
  (user, delegationOwners) =>
    _(delegationOwners)
      .concat(user)
      .map((t) => t.inboxTagId)
      .compact()
      .value(),
);

export const getUserStarredTagId = createSelector(getUser, (user) => user.starredTagId);

const getStablyOrderedStarredTagIds = wrapInShallowSelector(
  createSelector(getFlatTeammates, (teammates): readonly number[] => teammates.map((t) => t.starredTagId)),
);

// Warning: this is a big set for Better. Do not `wrapInShallowSelector` or `wrapInDeepEqualSelector`.
export const getStarredTagIds = createSelector(
  getStablyOrderedStarredTagIds,
  (starredTagIds): ReadonlySet<number> => new Set(starredTagIds),
);

// Only retrieve custom views the user has pinned.
export const getCustomViewTagIds = createSelector(getTags, (tags) =>
  Object.values(tags)
    .filter(isDefined)
    .filter((tag) => tag.behaviors.includes(TagBehaviorsEnum.CAN_CUSTOM_VIEW))
    .map((tag) => tag.id),
);

// The BE doesn't actually have custom views for the starred tag
// so that's our only way of finding out to what teammate a given
// starred tag belongs to.
export const getStarredTagIdsToTeammateMap = wrapInShallowSelector(
  createSelector(getFlatTeammates, (teammates) =>
    _(teammates)
      .keyBy((t) => t.starredTagId)
      .mapValues((t) => t.id)
      .value(),
  ),
);

export function makeGetConversationTagNamespaces<P>(getConversations: ConversationBasesSelector<P>) {
  return createSelector(
    [getUserNamespace, getUserTeams, getConversations],
    (userNamespace, userTeams, conversations) => {
      const [teammateConversations, teamConversations] = partition(conversations, isTeammateConversation);
      const firstTeamNamespace = teamConversations[0] && teamConversations[0].namespace;

      const isAllPrivate = teammateConversations.length > 0 && teamConversations.length === 0;
      const isAllSameTeam = teamConversations.every((tc) => tc.namespace === firstTeamNamespace);

      // If every conversation is a private inbox,
      // return the private namespace and all teams this user is a member of.
      if (isAllPrivate) {
        return [userNamespace, ...userTeams.map((t) => t.namespace)];
      }

      // If every conversation is from the same team,
      // return the private namespace and all the namespaces from the conversation selection.
      if (isAllSameTeam) {
        return uniq([userNamespace, ...teamConversations.map((c) => c.namespace)]);
      }

      return [userNamespace];
    },
  );
}

export function makeGetConversationInboxIdsForTags<P>(getConversations: ConversationBasesSelector<P>) {
  return createSelector([getUserOwnedInboxes, getConversations], (userOwnedInboxes, conversations) => {
    const [teammateConversations, teamConversations] = partition(conversations, isTeammateConversation);
    const firstTeamNamespace = teamConversations[0] && teamConversations[0].namespace;

    const isAllPrivate = teammateConversations.length > 0 && teamConversations.length === 0;
    const isAllSameTeam = teamConversations.every((tc) => tc.namespace === firstTeamNamespace);

    // If every conversation is a private inbox, return all the user's participating team inboxIds.
    if (isAllPrivate) {
      return userOwnedInboxes.map((i) => i.id);
    }

    // If every conversation is from the same team, return all the conversations' inboxIds.
    if (isAllSameTeam) {
      const conversationInboxIds = teamConversations.map((c) => c.inboxIds);
      return intersection(...conversationInboxIds);
    }

    // If the conversations span multiple conversations in mixed teams, return null.
    return null;
  });
}
